import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Ship, Plus, Search, Trash2, Edit3, FileDown,
  ArrowDownToLine, ArrowUpFromLine, Container, Coins, FileText,
} from 'lucide-react';
import ErpShell from '../components/ErpShell';
import { ErpPageHeader, ErpEmpty } from '../components/ErpPageHeader';
import { api } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { useUsers, shipmentFinance, PAY_STATUS_TONE, PAYMENT_METHODS, todayStr } from '../lib/financeShared';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const CARRIERS = ['DHL', 'FEDEX', 'UPS', 'Gilron', 'Orian', 'ZIM', 'Maersk', 'MSC', 'Other'];

const fmtMoney = (v) => (v === null || v === undefined || v === '' ? '—' : `₪${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);

const TABS = [
  { id: 'imports', label: 'Imports', icon: ArrowDownToLine, endpoint: '/imports', key: 'imports', category: 'IMPORT', kind: 'trade' },
  { id: 'exports', label: 'Exports', icon: ArrowUpFromLine, endpoint: '/exports', key: 'exports', category: 'EXPORT', kind: 'trade' },
  { id: 'adam', label: 'Adam Customers', icon: Container, endpoint: '/adam-customers', key: 'adamCustomers', category: 'ADAM_CUSTOMER', kind: 'adam' },
  { id: 'china', label: 'China Import', icon: Ship, endpoint: '/china-imports', key: 'chinaImports', category: 'CHINA_IMPORT', kind: 'china' },
  { id: 'invoices', label: 'Invoices', icon: FileText, endpoint: '/financial-documents', key: 'documents', category: 'FINANCIAL', kind: 'invoice' },
];

const FORM_FIELDS = {
  trade: [
    { k: 'customerName', label: 'Customer Name *' },
    { k: 'phone', label: 'Phone' },
    { k: 'idNumber', label: 'ID Number' },
    { k: 'trackingNumber', label: 'Tracking Number' },
    { k: 'description', label: 'Shipment (goods)' },
    { k: 'fileNumber', label: 'File Number' },
    { k: 'carrier', label: 'Shipping Company', list: CARRIERS },
    { k: 'clearanceCompany', label: 'Customs Clearance By' },
    { k: 'shippingCost', label: 'Cost — we pay shipper (₪)', type: 'number' },
    { k: 'amountRequested', label: 'Amount requested from customer (₪)', type: 'number' },
    { k: 'month', label: 'Month', select: MONTHS },
    { k: 'notes', label: 'Notes', full: true, textarea: true },
  ],
  adam: [
    { k: 'customerName', label: 'Customer Name *' },
    { k: 'phone', label: 'Phone' },
    { k: 'shipmentType', label: 'Type', select: ['CONTAINER', 'LCL', 'AIR'] },
    { k: 'trackingNumber', label: 'Tracking Number' },
    { k: 'containerNumber', label: 'Container #' },
    { k: 'blNumber', label: 'BL Number' },
    { k: 'portOrigin', label: 'Port of Origin' },
    { k: 'portDestination', label: 'Port of Destination' },
    { k: 'customsDeclarationNumber', label: 'Customs Declaration #' },
    { k: 'shippingCost', label: 'Cost (₪)', type: 'number' },
    { k: 'amountRequested', label: 'Amount requested (₪)', type: 'number' },
    { k: 'month', label: 'Month', select: MONTHS },
    { k: 'notes', label: 'Notes', full: true, textarea: true },
  ],
  china: [
    { k: 'customerName', label: 'Customer Name *' },
    { k: 'phone', label: 'Phone' },
    { k: 'trackingNumber', label: 'Tracking Number' },
    { k: 'description', label: 'Shipment (goods)' },
    { k: 'cbmPrice', label: 'Price per CBM — to customer (₪)', type: 'number' },
    { k: 'costPerCbm', label: 'Cost per CBM — from shipper (₪)', type: 'number' },
    { k: 'totalCbm', label: 'Total CBM', type: 'number' },
    { k: 'month', label: 'Month', select: MONTHS },
    { k: 'notes', label: 'Notes', full: true, textarea: true },
  ],
  invoice: [
    { k: 'customerName', label: 'Customer Name *' },
    { k: 'invoiceNumber', label: 'Invoice Number' },
    { k: 'amount', label: 'Amount', type: 'number' },
    { k: 'currency', label: 'Currency', select: ['ILS', 'USD', 'EUR'] },
    { k: 'dueDate', label: 'Due Date', type: 'date' },
    { k: 'month', label: 'Month', select: MONTHS },
    { k: 'paymentStatus', label: 'Payment Status', select: ['UNPAID', 'PAID'] },
    { k: 'notes', label: 'Notes', full: true, textarea: true },
  ],
};

const statusChip = (r) => {
  const fin = shipmentFinance(r);
  let status = fin.status;
  // Fallback to manual status (invoices) when there is no requested/paid data
  if (fin.requested === 0 && fin.paid === 0 && r.paymentStatus) status = r.paymentStatus;
  const tone = PAY_STATUS_TONE[status] || 'warning';
  return <span className={`gg-chip gg-chip-${tone}`}>{status}</span>;
};

const emptyForm = (kind) => {
  const f = {};
  FORM_FIELDS[kind].forEach((fd) => { f[fd.k] = fd.k === 'paymentStatus' ? 'UNPAID' : fd.k === 'currency' ? 'ILS' : ''; });
  f.owner = '';
  f.followUpDate = '';
  return f;
};

export default function ErpShipments() {
  const { toast } = useToast();
  const users = useUsers();
  const [searchParams] = useSearchParams();
  const paramTab = searchParams.get('tab');
  const [tabId, setTabId] = useState(TABS.some((t) => t.id === paramTab) ? paramTab : 'imports');
  const tab = TABS.find((t) => t.id === tabId);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [month, setMonth] = useState('All');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm('trade'));
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [payModal, setPayModal] = useState(null); // { record, data }
  const [payForm, setPayForm] = useState({ amount: '', date: todayStr(), method: 'Cash', note: '' });
  const [payBusy, setPayBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(tab.endpoint);
      setRows(r.data[tab.key] || []);
    } catch { toast({ title: 'Failed to load', variant: 'destructive' }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); setQ(''); setMonth('All'); }, [tabId]);

  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && TABS.some((x) => x.id === t) && t !== tabId) setTabId(t);
  }, [searchParams]);

  const downloadPdf = async (r) => {
    const { status } = shipmentFinance(r);
    const type = status === 'PAID' ? 'receipt' : 'invoice';
    try {
      const res = await api.get(`/records/${r.id}/pdf?doc_type=${type}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type === 'receipt' ? 'Receipt' : 'Invoice'}_${r.invoiceNumber || r.fileNumber || r.trackingNumber || r.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast({ title: `${type === 'receipt' ? 'Receipt' : 'Invoice'} downloaded` });
    } catch { toast({ title: 'Failed to generate PDF', variant: 'destructive' }); }
  };

  const openPay = async (r) => {
    setPayForm({ amount: '', date: todayStr(), method: 'Cash', note: '' });
    setPayModal({ record: r, loading: true });
    try {
      const res = await api.get(`/records/${r.id}/payments`);
      setPayModal({ record: r, ...res.data });
    } catch { setPayModal({ record: r, payments: [] }); }
  };

  const addPayment = async () => {
    const amount = parseFloat(payForm.amount);
    if (!amount || amount <= 0) { toast({ title: 'Enter a valid amount', variant: 'destructive' }); return; }
    setPayBusy(true);
    try {
      const res = await api.post(`/records/${payModal.record.id}/payments`, { ...payForm, amount });
      const rec = res.data.record;
      setPayModal({ record: rec, payments: rec.payments || [], amountRequested: rec.amountRequested, amountPaid: rec.amountPaid, balanceLeft: rec.balanceLeft, paymentStatus: rec.paymentStatus });
      setPayForm({ amount: '', date: todayStr(), method: 'Cash', note: '' });
      setRows((rs) => rs.map((x) => x.id === rec.id ? { ...x, ...rec } : x));
      toast({ title: 'Payment added' });
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed to add payment', variant: 'destructive' }); }
    finally { setPayBusy(false); }
  };

  const deletePayment = async (pid) => {
    try {
      const res = await api.delete(`/records/${payModal.record.id}/payments/${pid}`);
      const rec = res.data.record;
      setPayModal({ record: rec, payments: rec.payments || [], amountRequested: rec.amountRequested, amountPaid: rec.amountPaid, balanceLeft: rec.balanceLeft, paymentStatus: rec.paymentStatus });
      setRows((rs) => rs.map((x) => x.id === rec.id ? { ...x, ...rec } : x));
      toast({ title: 'Payment removed' });
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  const filtered = useMemo(() => rows.filter((r) => {
    if (month !== 'All' && (r.month || '') !== month) return false;
    if (q.trim()) {
      const t = q.toLowerCase();
      return [r.customerName, r.trackingNumber, r.phone, r.description, r.fileNumber, r.carrier, r.clearanceCompany, r.invoiceNumber]
        .some((v) => (v || '').toString().toLowerCase().includes(t));
    }
    return true;
  }), [rows, q, month]);

  const totals = useMemo(() => {
    let cost = 0, requested = 0, paid = 0;
    filtered.forEach((r) => {
      cost += Number(r.shippingCost) || 0;
      const f = shipmentFinance(r);
      requested += f.requested;
      paid += f.paid;
    });
    const amount = filtered.reduce((a, r) => a + (Number(r.amount) || 0), 0);
    return { cost, requested, paid, left: requested - paid, amount };
  }, [filtered]);

  const openNew = () => { setForm(emptyForm(tab.kind)); setEditingId(null); setShowForm(true); };
  const openEdit = (r) => {
    const f = emptyForm(tab.kind);
    Object.keys(f).forEach((k) => { f[k] = r[k] === null || r[k] === undefined ? '' : String(r[k]); });
    // China: derive per-CBM cost from stored totals when not present
    if (tab.kind === 'china' && !f.costPerCbm && Number(r.shippingCost) && Number(r.totalCbm)) {
      f.costPerCbm = String(Number(r.shippingCost) / Number(r.totalCbm));
    }
    setForm(f);
    setEditingId(r.id);
    setShowForm(true);
  };

  // For China import, pricing is per-CBM — compute total cost & requested from per-CBM values
  const chinaPayload = (f) => {
    const cbm = parseFloat(f.totalCbm) || 0;
    return {
      ...f,
      shippingCost: (parseFloat(f.costPerCbm) || 0) * cbm,
      amountRequested: (parseFloat(f.cbmPrice) || 0) * cbm,
    };
  };

  const save = async () => {
    if (!form.customerName.trim()) {
      toast({ title: 'Customer name required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    const payload = tab.kind === 'china' ? chinaPayload(form) : form;
    try {
      if (editingId) {
        await api.put(`/documents/${editingId}`, payload);
        toast({ title: 'Record updated' });
      } else {
        await api.post('/records', { ...payload, category: tab.category });
        toast({ title: 'Record added' });
      }
      setShowForm(false);
      load();
    } catch { toast({ title: 'Failed to save', variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this record?')) return;
    try {
      await api.delete(`/documents/${id}`);
      toast({ title: 'Record deleted' });
      load();
    } catch { toast({ title: 'Failed to delete', variant: 'destructive' }); }
  };

  const chinaCbm = parseFloat(form.totalCbm) || 0;
  const chinaCostTotal = (parseFloat(form.costPerCbm) || 0) * chinaCbm;
  const chinaReqTotal = (parseFloat(form.cbmPrice) || 0) * chinaCbm;
  const liveProfit = tab.kind === 'china'
    ? chinaReqTotal - chinaCostTotal
    : (parseFloat(form.amountRequested) || 0) - (parseFloat(form.shippingCost) || 0);

  return (
    <ErpShell title="Shipments">
      <ErpPageHeader
        eyebrow="Operations"
        title="Shipments"
        subtitle="Imports, exports and all shipment records."
        icon={Ship}
        actions={
          <>
            <div className="relative">
              <Search size={14} className="absolute start-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--gg-text-dim)' }} />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, tracking…" className="gg-input ps-9" style={{ width: 230 }} data-testid="records-search" />
            </div>
            <select value={month} onChange={(e) => setMonth(e.target.value)} className="gg-input" style={{ width: 150 }} data-testid="records-month-filter">
              <option value="All">All months</option>
              {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <button onClick={openNew} className="gg-btn-primary" data-testid="new-record-btn">
              <Plus size={14} /> New {tab.kind === 'invoice' ? 'Invoice' : 'Record'}
            </button>
          </>
        }
      />

      {/* Tabs */}
      <div className="flex gap-2 mb-5 flex-wrap" data-testid="records-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = t.id === tabId;
          return (
            <button
              key={t.id}
              onClick={() => setTabId(t.id)}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-semibold transition-all"
              style={active
                ? { background: '#0A1F3C', color: '#E2C15B', boxShadow: '0 6px 18px -6px rgba(10,31,60,0.5)' }
                : { background: 'var(--gg-surface-2)', color: 'var(--gg-text-secondary)' }}
              data-testid={`tab-${t.id}`}
            >
              <Icon size={13} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {tab.kind === 'invoice' ? (
          <>
            <Stat label="Invoices" value={filtered.length} />
            <Stat label="Total Amount" value={fmtMoney(totals.amount)} />
            <Stat label="Paid" value={filtered.filter((r) => shipmentFinance(r).status === 'PAID').length} tone="emerald" />
            <Stat label="Unpaid" value={filtered.filter((r) => shipmentFinance(r).status !== 'PAID').length} tone="danger" />
          </>
        ) : (
          <>
            <Stat label="Cost (we pay)" value={fmtMoney(totals.cost)} tone="danger" />
            <Stat label="Requested" value={fmtMoney(totals.requested)} />
            <Stat label="Paid" value={fmtMoney(totals.paid)} tone="emerald" />
            <Stat label="Balance left" value={fmtMoney(totals.left)} tone={totals.left > 0 ? 'danger' : 'emerald'} />
          </>
        )}
      </div>

      {loading ? (
        <div className="gg-card p-12 animate-pulse" />
      ) : filtered.length === 0 ? (
        <ErpEmpty
          icon={tab.icon}
          title={q || month !== 'All' ? 'No records match your filters' : `No ${tab.label.toLowerCase()} yet`}
          subtitle="Add your first record — it takes seconds."
          action={<button onClick={openNew} className="gg-btn-primary"><Plus size={14} /> Add Record</button>}
        />
      ) : (
        <div className="gg-card overflow-hidden" data-testid="records-table">
          <div className="overflow-x-auto">
            <table className="gg-table" style={{ minWidth: tab.kind === 'trade' ? 1100 : 900 }}>
              <thead>
                <tr>
                  {tab.kind === 'trade' && <>
                    <th>Month</th><th>Customer</th><th>ID #</th><th>Tracking #</th><th>Shipment</th>
                    <th>Shipping Co.</th><th>Clearance By</th><th>File #</th>
                    <th>Cost</th><th>Requested</th><th>Paid</th><th>Left</th><th>Status</th>
                  </>}
                  {tab.kind === 'adam' && <>
                    <th>Month</th><th>Customer</th><th>Type</th><th>Tracking / BL</th><th>Container</th>
                    <th>Route</th><th>Cost</th><th>Requested</th><th>Paid</th><th>Left</th><th>Status</th>
                  </>}
                  {tab.kind === 'china' && <>
                    <th>Month</th><th>Customer</th><th>Tracking #</th><th>Shipment</th>
                    <th>CBM ₪</th><th>Total CBM</th><th>Cost</th><th>Requested</th><th>Paid</th><th>Left</th><th>Status</th>
                  </>}
                  {tab.kind === 'invoice' && <>
                    <th>Invoice #</th><th>Customer</th><th>Amount</th><th>Due</th><th>Status</th>
                  </>}
                  <th style={{ width: 80 }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, idx) => {
                  const fin = shipmentFinance(r);
                  const reqCell = <td className="gg-tabular font-semibold">{fin.requested ? fmtMoney(fin.requested) : '—'}</td>;
                  const paidCell = <td className="gg-tabular font-semibold" style={{ color: 'var(--gg-emerald)' }}>{fin.paid ? fmtMoney(fin.paid) : '—'}</td>;
                  const leftCell = <td className="gg-tabular font-semibold" style={{ color: fin.left > 0 ? 'var(--gg-danger)' : 'var(--gg-text-dim)' }}>{fin.requested ? fmtMoney(fin.left) : '—'}</td>;
                  return (
                    <motion.tr key={r.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, delay: Math.min(idx * 0.015, 0.25) }} data-testid={`record-row-${r.id}`}>
                      {tab.kind === 'trade' && <>
                        <td style={{ color: 'var(--gg-text-dim)' }}>{r.month || '—'}</td>
                        <td>
                          <p className="font-semibold">{r.customerName || '—'}</p>
                          <p className="text-xs" style={{ color: 'var(--gg-text-dim)' }}>{r.phone || ''}</p>
                        </td>
                        <td className="gg-tabular" style={{ color: 'var(--gg-text-dim)' }}>{r.idNumber || '—'}</td>
                        <td className="gg-tabular">{r.trackingNumber || '—'}</td>
                        <td className="max-w-[180px] truncate" style={{ color: 'var(--gg-text-secondary)' }}>{r.description || r.title || '—'}</td>
                        <td>{r.carrier ? <span className="gg-chip gg-chip-info">{r.carrier}</span> : '—'}</td>
                        <td style={{ color: 'var(--gg-text-secondary)' }}>{r.clearanceCompany || '—'}</td>
                        <td className="gg-tabular" style={{ color: 'var(--gg-text-dim)' }}>{r.fileNumber || '—'}</td>
                        <td className="gg-tabular" style={{ color: 'var(--gg-danger)' }}>{fmtMoney(r.shippingCost)}</td>
                        {reqCell}{paidCell}{leftCell}
                        <td>{statusChip(r)}</td>
                      </>}
                      {tab.kind === 'adam' && <>
                        <td style={{ color: 'var(--gg-text-dim)' }}>{r.month || '—'}</td>
                        <td>
                          <p className="font-semibold">{r.customerName || '—'}</p>
                          <p className="text-xs" style={{ color: 'var(--gg-text-dim)' }}>{r.phone || ''}</p>
                        </td>
                        <td><span className="gg-chip gg-chip-neutral">{r.shipmentType || '—'}</span></td>
                        <td className="gg-tabular">{r.trackingNumber || r.blNumber || '—'}</td>
                        <td className="gg-tabular" style={{ color: 'var(--gg-text-dim)' }}>{r.containerNumber || '—'}</td>
                        <td className="text-xs" style={{ color: 'var(--gg-text-secondary)' }}>{[r.portOrigin, r.portDestination].filter(Boolean).join(' → ') || '—'}</td>
                        <td className="gg-tabular" style={{ color: 'var(--gg-danger)' }}>{fmtMoney(r.shippingCost)}</td>
                        {reqCell}{paidCell}{leftCell}
                        <td>{statusChip(r)}</td>
                      </>}
                      {tab.kind === 'china' && <>
                        <td style={{ color: 'var(--gg-text-dim)' }}>{r.month || '—'}</td>
                        <td className="font-semibold">{r.customerName || '—'}</td>
                        <td className="gg-tabular">{r.trackingNumber || '—'}</td>
                        <td className="max-w-[180px] truncate" style={{ color: 'var(--gg-text-secondary)' }}>{r.description || '—'}</td>
                        <td className="gg-tabular">{fmtMoney(r.cbmPrice)}</td>
                        <td className="gg-tabular">{r.totalCbm || '—'}</td>
                        <td className="gg-tabular" style={{ color: 'var(--gg-danger)' }}>{fmtMoney(r.shippingCost)}</td>
                        {reqCell}{paidCell}{leftCell}
                        <td>{statusChip(r)}</td>
                      </>}
                      {tab.kind === 'invoice' && <>
                        <td className="gg-tabular font-semibold">{r.invoiceNumber || '—'}</td>
                        <td>{r.customerName || '—'}</td>
                        <td className="gg-tabular font-semibold">{r.currency === 'USD' ? '$' : r.currency === 'EUR' ? '€' : '₪'}{(Number(r.amount) || 0).toLocaleString()}</td>
                        <td className="gg-tabular" style={{ color: 'var(--gg-text-dim)' }}>{r.dueDate || '—'}</td>
                        <td>{statusChip(r)}</td>
                      </>}
                      <td>
                        <div className="flex gap-1 justify-end">
                          {tab.kind !== 'invoice' && (
                            <button onClick={() => openPay(r)} className="gg-btn-ghost" style={{ color: 'var(--gg-emerald)' }} title="Payments" data-testid={`pay-record-${r.id}`}>
                              <Coins size={13} />
                            </button>
                          )}
                          <button onClick={() => downloadPdf(r)}
                            className="gg-btn-ghost"
                            style={{ color: 'var(--gg-accent)' }}
                            title="Download PDF"
                            data-testid={`pdf-record-${r.id}`}
                          >
                            <FileDown size={13} />
                          </button>
                          <button onClick={() => openEdit(r)} className="gg-btn-ghost" data-testid={`edit-record-${r.id}`}><Edit3 size={13} /></button>
                          <button onClick={() => remove(r.id)} className="gg-btn-ghost" style={{ color: 'var(--gg-danger)' }} data-testid={`delete-record-${r.id}`}><Trash2 size={13} /></button>
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Form modal */}
      {showForm && (
        <div className="gg-cmdk-overlay flex items-start justify-center pt-14 overflow-y-auto" onClick={() => !saving && setShowForm(false)}>
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="gg-cmdk-panel p-6 mb-10"
            style={{ width: 'min(760px, 92vw)', maxHeight: '85vh', overflowY: 'auto' }}
            data-testid="record-form"
          >
            <p className="gg-eyebrow" style={{ color: 'var(--gg-accent)' }}>{tab.label}</p>
            <h2 className="gg-hero-title" style={{ fontSize: 24 }}>{editingId ? 'Update' : 'Add'} {tab.kind === 'invoice' ? 'Invoice' : 'Record'}</h2>
            <div className="mt-5 grid grid-cols-2 gap-3">
              {FORM_FIELDS[tab.kind].map((fd) => (
                <div key={fd.k} className={fd.full ? 'col-span-2' : ''}>
                  <label className="gg-eyebrow block mb-1">{fd.label}</label>
                  {fd.select ? (
                    <select value={form[fd.k]} onChange={(e) => setForm({ ...form, [fd.k]: e.target.value })} className="gg-input" data-testid={`form-${fd.k}`}>
                      {fd.k === 'month' && <option value="">— No month —</option>}
                      {fd.select.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : fd.textarea ? (
                    <textarea value={form[fd.k]} onChange={(e) => setForm({ ...form, [fd.k]: e.target.value })} className="gg-input" rows={2} style={{ height: 'auto', padding: '10px 14px' }} data-testid={`form-${fd.k}`} />
                  ) : (
                    <>
                      <input
                        type={fd.type || 'text'}
                        value={form[fd.k]}
                        onChange={(e) => setForm({ ...form, [fd.k]: e.target.value })}
                        className="gg-input"
                        list={fd.list ? `list-${fd.k}` : undefined}
                        data-testid={`form-${fd.k}`}
                      />
                      {fd.list && (
                        <datalist id={`list-${fd.k}`}>
                          {fd.list.map((o) => <option key={o} value={o} />)}
                        </datalist>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
            {/* Owner + follow-up (feature 5) */}
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div>
                <label className="gg-eyebrow block mb-1">Owner</label>
                <select value={form.owner || ''} onChange={(e) => setForm({ ...form, owner: e.target.value })} className="gg-input" data-testid="form-owner">
                  <option value="">— Owner —</option>
                  {users.map((u) => <option key={u.id} value={u.name}>{u.name}</option>)}
                </select>
              </div>
              <div>
                <label className="gg-eyebrow block mb-1">Next follow-up</label>
                <input type="date" value={form.followUpDate || ''} onChange={(e) => setForm({ ...form, followUpDate: e.target.value })} className="gg-input" data-testid="form-followUpDate" />
              </div>
            </div>
            {tab.kind === 'china' ? (
              (form.cbmPrice || form.costPerCbm || form.totalCbm) && (
                <div className="mt-4 rounded-xl px-4 py-3 space-y-1" style={{ background: liveProfit >= 0 ? 'var(--gg-emerald-soft)' : 'rgba(255,59,48,0.1)' }} data-testid="live-profit">
                  <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--gg-text-secondary)' }}>
                    <Coins size={14} style={{ color: 'var(--gg-accent)' }} />
                    <span>{chinaCbm || 0} CBM × cost {fmtMoney(parseFloat(form.costPerCbm) || 0)} = <b>{fmtMoney(chinaCostTotal)}</b> total cost</span>
                  </div>
                  <div className="text-sm" style={{ color: 'var(--gg-text-secondary)' }}>
                    {chinaCbm || 0} CBM × price {fmtMoney(parseFloat(form.cbmPrice) || 0)} = <b>{fmtMoney(chinaReqTotal)}</b> requested from customer
                  </div>
                  <div className="text-sm font-semibold pt-1" style={{ color: liveProfit >= 0 ? 'var(--gg-emerald)' : 'var(--gg-danger)' }}>
                    Profit: {fmtMoney(liveProfit)} ({fmtMoney((parseFloat(form.cbmPrice) || 0) - (parseFloat(form.costPerCbm) || 0))}/CBM × {chinaCbm || 0})
                  </div>
                </div>
              )
            ) : (form.amountRequested || form.shippingCost) && (
              <div className="mt-4 flex items-center gap-2 rounded-xl px-4 py-3" style={{ background: liveProfit >= 0 ? 'var(--gg-emerald-soft)' : 'rgba(255,59,48,0.1)' }} data-testid="live-profit">
                <Coins size={14} style={{ color: liveProfit >= 0 ? 'var(--gg-emerald)' : 'var(--gg-danger)' }} />
                <span className="text-sm font-semibold" style={{ color: liveProfit >= 0 ? 'var(--gg-emerald)' : 'var(--gg-danger)' }}>
                  Expected profit (requested − cost): {fmtMoney(liveProfit)}
                </span>
              </div>
            )}
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowForm(false)} disabled={saving} className="gg-btn-secondary" data-testid="form-cancel">Cancel</button>
              <button onClick={save} disabled={saving} className="gg-btn-primary" data-testid="form-save">
                {saving ? 'Saving…' : editingId ? 'Update' : 'Save'}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Per-shipment payments modal */}
      {payModal && (
        <div className="gg-cmdk-overlay flex items-start justify-center pt-16 overflow-y-auto" onClick={() => setPayModal(null)}>
          <motion.div initial={{ opacity: 0, y: 10, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} onClick={(e) => e.stopPropagation()} className="gg-cmdk-panel p-6 mb-10" style={{ width: 'min(560px, 94vw)', maxHeight: '86vh', overflowY: 'auto' }} data-testid="pay-modal">
            <p className="gg-eyebrow" style={{ color: 'var(--gg-accent)' }}>Payments</p>
            <h2 className="gg-hero-title" style={{ fontSize: 22 }}>{payModal.record.customerName || 'Shipment'}</h2>
            <p className="text-xs mb-4" style={{ color: 'var(--gg-text-dim)' }}>{payModal.record.trackingNumber || payModal.record.fileNumber || payModal.record.id?.slice(0, 8)}</p>

            <div className="grid grid-cols-3 gap-2 mb-4">
              <div className="rounded-xl p-3 text-center" style={{ background: 'var(--gg-surface-2)' }}>
                <p className="gg-eyebrow">Requested</p>
                <p className="gg-tabular font-bold" style={{ fontSize: 18 }} data-testid="pay-requested">{fmtMoney(payModal.amountRequested)}</p>
              </div>
              <div className="rounded-xl p-3 text-center" style={{ background: 'var(--gg-emerald-soft)' }}>
                <p className="gg-eyebrow" style={{ color: 'var(--gg-emerald)' }}>Paid</p>
                <p className="gg-tabular font-bold" style={{ fontSize: 18, color: 'var(--gg-emerald)' }} data-testid="pay-paid">{fmtMoney(payModal.amountPaid)}</p>
              </div>
              <div className="rounded-xl p-3 text-center" style={{ background: (payModal.balanceLeft || 0) > 0 ? 'rgba(255,59,48,0.1)' : 'var(--gg-emerald-soft)' }}>
                <p className="gg-eyebrow" style={{ color: (payModal.balanceLeft || 0) > 0 ? 'var(--gg-danger)' : 'var(--gg-emerald)' }}>Left</p>
                <p className="gg-tabular font-bold" style={{ fontSize: 18, color: (payModal.balanceLeft || 0) > 0 ? 'var(--gg-danger)' : 'var(--gg-emerald)' }} data-testid="pay-left">{fmtMoney(payModal.balanceLeft)}</p>
              </div>
            </div>

            {/* Add payment */}
            <div className="rounded-2xl p-4 mb-4" style={{ background: 'var(--gg-surface-2)' }}>
              <p className="gg-eyebrow mb-2">Add payment</p>
              <div className="grid grid-cols-2 gap-2">
                <input type="number" placeholder="Amount ₪" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} className="gg-input" data-testid="pay-amount-input" />
                <input type="date" value={payForm.date} onChange={(e) => setPayForm({ ...payForm, date: e.target.value })} className="gg-input" data-testid="pay-date-input" />
                <select value={payForm.method} onChange={(e) => setPayForm({ ...payForm, method: e.target.value })} className="gg-input" data-testid="pay-method-input">
                  {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
                <input placeholder="Note (optional)" value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} className="gg-input" data-testid="pay-note-input" />
              </div>
              <button onClick={addPayment} disabled={payBusy} className="gg-btn-primary w-full justify-center mt-3" data-testid="pay-add-btn">
                <Plus size={14} /> {payBusy ? 'Adding…' : 'Add payment'}
              </button>
            </div>

            {/* Ledger */}
            {payModal.loading ? (
              <div className="h-20 animate-pulse rounded-xl" style={{ background: 'var(--gg-surface-2)' }} />
            ) : (payModal.payments || []).length === 0 ? (
              <p className="text-sm py-3 text-center" style={{ color: 'var(--gg-text-dim)' }}>No payments yet.</p>
            ) : (
              <div className="space-y-1" data-testid="pay-ledger">
                {payModal.payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between text-sm py-2 px-1" style={{ borderTop: '1px solid var(--gg-border)' }}>
                    <span style={{ color: 'var(--gg-text-secondary)' }}>{p.date} · {p.method}{p.note ? ` · ${p.note}` : ''}</span>
                    <span className="flex items-center gap-2">
                      <span className="gg-tabular font-semibold" style={{ color: 'var(--gg-emerald)' }}>{fmtMoney(p.amount)}</span>
                      <button onClick={() => deletePayment(p.id)} className="gg-btn-ghost" style={{ color: 'var(--gg-danger)' }} data-testid={`pay-del-${p.id}`}><Trash2 size={12} /></button>
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end mt-6">
              <button onClick={() => setPayModal(null)} className="gg-btn-secondary" data-testid="pay-close">Close</button>
            </div>
          </motion.div>
        </div>
      )}
    </ErpShell>
  );
}

const Stat = ({ label, value, tone }) => (
  <div className="gg-card p-4">
    <p className="gg-eyebrow">{label}</p>
    <p className="gg-tabular mt-1" style={{ fontSize: 24, fontWeight: 700, color: tone === 'emerald' ? 'var(--gg-emerald)' : tone === 'danger' ? 'var(--gg-danger)' : 'var(--gg-text)' }}>
      {value}
    </p>
  </div>
);
